import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/Button';
import { ToastCard } from '../ui/Toast';
import Tooltip from '../ui/Tooltip';
import { MS } from '../ui/motion';
import type { ToastAction, ToastApi, ToastOptions, ToastShow, ToastType } from './types';

export type { ToastAction, ToastApi, ToastOptions, ToastType } from './types';

const ToastContext = createContext<ToastApi | null>(null);

/**
 * The toast methods: `toast.success('Saved')`, `toast.error('Could not save', 8000)`,
 * `toast.success('Line removed', { action: { label: 'Undo', tip: 'Put the line back', onClick } })`.
 */
export const useToast = (): ToastApi => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const MAX_SHOWN = 4;
/** A toast the pointer or focus held always gets at least this long once released. */
const RESUME_FLOOR_MS = 2000;
/** The card fades on duration-exit while its slot closes on duration-standard; it unmounts after both. */
const LEAVE_MS = MS.standard + 20;

interface Entry {
  id: number;
  message: string;
  type: ToastType;
  action?: ToastAction;
  exiting: boolean;
}

interface Clock {
  handle?: number;
  deadline: number;
  remaining: number;
}

let mountedApi: ToastApi | null = null;

// No provider mounted (a bare test, a page outside the app shell): the message still reaches the person.
const unmounted: ToastShow = (message) => {
  if (typeof window !== 'undefined' && typeof window.alert === 'function') window.alert(String(message));
  return 0;
};

/**
 * The same four methods for code outside React (apiHelpers' success and error alerts). They
 * reach whichever ToastProvider is mounted.
 */
export const notify: ToastApi = {
  success: (m, o) => (mountedApi ? mountedApi.success(m, o) : unmounted(m, o)),
  error: (m, o) => (mountedApi ? mountedApi.error(m, o) : unmounted(m, o)),
  warning: (m, o) => (mountedApi ? mountedApi.warning(m, o) : unmounted(m, o)),
  info: (m, o) => (mountedApi ? mountedApi.info(m, o) : unmounted(m, o)),
};

export interface ToastProviderProps {
  children?: React.ReactNode;
  /** Fires for every toast shown; the kiosk uses it to buzz on errors. */
  onShow?: (type: ToastType) => void;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children, onShow }) => {
  const [toasts, setToasts] = useState<Entry[]>([]);
  // The list as last committed, so a toast fired twice in one tick sees the first.
  const list = useRef<Entry[]>([]);
  const onShowRef = useRef(onShow);
  onShowRef.current = onShow;
  const nextId = useRef(0);
  const clocks = useRef(new Map<number, Clock>());
  const leaving = useRef(new Map<number, number>());

  useEffect(() => {
    const running = clocks.current;
    const exits = leaving.current;
    return () => {
      running.forEach((c) => window.clearTimeout(c.handle));
      exits.forEach((h) => window.clearTimeout(h));
    };
  }, []);

  const commit = useCallback((next: Entry[]) => {
    list.current = next;
    setToasts(next);
  }, []);

  const remove = useCallback((id: number) => {
    const clock = clocks.current.get(id);
    if (clock) {
      window.clearTimeout(clock.handle);
      clocks.current.delete(id);
    }
    if (leaving.current.has(id) || !list.current.some((t) => t.id === id)) return;
    commit(list.current.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
    leaving.current.set(id, window.setTimeout(() => {
      leaving.current.delete(id);
      commit(list.current.filter((t) => t.id !== id));
    }, LEAVE_MS));
  }, [commit]);

  const start = useCallback((id: number, ms: number) => {
    const handle = window.setTimeout(() => remove(id), ms);
    clocks.current.set(id, { handle, deadline: Date.now() + ms, remaining: ms });
  }, [remove]);

  const pause = useCallback((id: number) => {
    const clock = clocks.current.get(id);
    if (!clock || clock.handle === undefined) return;
    window.clearTimeout(clock.handle);
    clocks.current.set(id, { deadline: clock.deadline, remaining: Math.max(0, clock.deadline - Date.now()) });
  }, []);

  const resume = useCallback((id: number) => {
    const clock = clocks.current.get(id);
    if (!clock || clock.handle !== undefined || leaving.current.has(id)) return;
    start(id, Math.max(clock.remaining, RESUME_FLOOR_MS));
  }, [start]);

  const add = useCallback((message: unknown, type: ToastType, options: number | ToastOptions | undefined, defaultMs: number): number => {
    const opts: ToastOptions = typeof options === 'object' && options !== null ? options : { duration: options as number | undefined };
    const duration = opts.duration ?? (opts.action ? 8000 : defaultMs);
    const id = ++nextId.current;
    onShowRef.current?.(type);
    commit([...list.current, {
      id,
      message: typeof message === 'string' ? message : String(message),
      type,
      action: opts.action,
      exiting: false,
    }]);
    if (duration > 0) start(id, duration);
    // Past the cap, the oldest leave the way any toast leaves rather than vanishing.
    const shown = list.current.filter((t) => !t.exiting);
    shown.slice(0, Math.max(0, shown.length - MAX_SHOWN)).forEach((t) => remove(t.id));
    return id;
  }, [commit, remove, start]);

  const api = useMemo<ToastApi>(() => ({
    success: (m, o) => add(m, 'success', o, 4000),
    error: (m, o) => add(m, 'error', o, 6000),
    warning: (m, o) => add(m, 'warning', o, 4000),
    info: (m, o) => add(m, 'info', o, 4000),
  }), [add]);

  useEffect(() => {
    mountedApi = api;
    return () => {
      if (mountedApi === api) mountedApi = null;
    };
  }, [api]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Portalled: a transform or filter on an ancestor would otherwise trap the fixed stack inside it. */}
      {typeof document !== 'undefined' && createPortal(
        <div className="pointer-events-none fixed inset-x-0 top-4 z-[55] flex flex-col items-center px-4">
          {toasts.map((t) => (
            <ToastSlot key={t.id} toast={t} onDismiss={remove} onPause={pause} onResume={resume} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

interface SlotProps {
  toast: Entry;
  onDismiss: (id: number) => void;
  onPause: (id: number) => void;
  onResume: (id: number) => void;
}

/** One toast in the stack. Its slot closes after it leaves, so the toasts under it rise instead of jumping. */
const ToastSlot: React.FC<SlotProps> = ({ toast, onDismiss, onPause, onResume }) => {
  const { id, exiting, action } = toast;
  const [arrived, setArrived] = useState(false);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    // Two frames: the closed state has to paint once or nothing animates.
    if (typeof requestAnimationFrame !== 'function') {
      setArrived(true);
      return undefined;
    }
    let second = 0;
    const first = requestAnimationFrame(() => { second = requestAnimationFrame(() => setArrived(true)); });
    return () => { cancelAnimationFrame(first); cancelAnimationFrame(second); };
  }, []);

  // The clock stops while the pointer rests on the toast or focus is inside it.
  const held = hover || focus;
  useEffect(() => {
    if (held) onPause(id);
    else onResume(id);
  }, [held, id, onPause, onResume]);

  return (
    <div className={`grid w-80 transition-[grid-template-rows] motion-reduce:transition-none max-sm:w-full ${exiting ? 'grid-rows-[0fr] duration-standard ease-exit' : 'grid-rows-[1fr]'}`}>
      <div className={`-mx-4 -mt-2 min-h-0 px-4 pt-2 ${exiting ? 'overflow-hidden' : ''}`}>
        <div className="pb-2">
          <ToastCard
            tone={toast.type}
            open={arrived && !exiting}
            role="alert"
            onDismiss={() => onDismiss(id)}
            action={action ? (
              <Tooltip label={action.tip}>
                <Button variant="tonal" size="sm" onClick={() => { action.onClick(); onDismiss(id); }}>{action.label}</Button>
              </Tooltip>
            ) : undefined}
            onPointerEnter={() => setHover(true)}
            onPointerLeave={() => setHover(false)}
            onFocus={() => setFocus(true)}
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocus(false); }}
          >
            {toast.message}
          </ToastCard>
        </div>
      </div>
    </div>
  );
};

export default ToastProvider;
